import type { QueryResult } from '@azure/digital-twins-core'
import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { BasicRelationship } from 'models/BasicRelationship'
import type {
  FeatureParameterConfig,
  FeatureParameterValues,
  ParameterValue
} from 'models/FeatureParameter'
import { jexlEvalString } from 'utils/jexl'

export interface QueryResponseData {
  /** All twins extracted from query */
  twins: BasicDigitalTwin[]
  /** All relationships extracted from query */
  relationships: BasicRelationship[]
  /** Returned count (in case of a COUNT query) or total number of twins */
  count: number
  /** Objects in the query that aren not a twin nor a relationship */
  other: unknown[]
  /** The raw query response */
  data: Record<string, unknown>[]
}

/** Extract all twins and relationships from a query response */
export const getDataFromQueryResponse = (response: QueryResult['value']): QueryResponseData => {
  const result: QueryResponseData = {
    twins: [],
    relationships: [],
    count: 0,
    other: [],
    data: response ? [...response] : []
  }
  if (!response || response.length === 0) return result

  const list = [...response]

  for (const current of list) {
    if (current.$dtId) {
      if (!result.twins.some((x) => x.$dtId === current.$dtId))
        result.twins.push(current as BasicDigitalTwin)
      continue
    } else if (current.$relationshipId) {
      if (
        !result.relationships.some(
          (x) => x.$sourceId === current.$sourceId && x.$relationshipId === current.$relationshipId
        )
      )
        result.relationships.push(current as BasicRelationship)
      continue
    } else if (typeof current.COUNT === 'number' && !Number.isNaN(current.COUNT)) {
      result.count = current.COUNT
      continue
    }

    for (const k of Object.keys(current)) {
      const v = current[k]
      if (Array.isArray(v)) {
        v.forEach((x) => list.push(x))
      } else if (typeof v === 'object' && v !== null && Object.getOwnPropertyNames(v).length > 0) {
        list.push(v as Record<string, unknown>)
      } else {
        result.other.push(v)
      }
    }
  }
  if (!result.count && result.twins.length > 0) result.count = result.twins.length

  return result
}

/** Render a typed value as ADT query text: arrays become a bracketed literal, scalars their string form */
const toAdtLiteral = (value: ParameterValue | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value
  if (Array.isArray(value) || typeof value === 'object') return JSON.stringify(value)
  return `${value}`
}

/** Generate a query by evaluating a JEXL expression (start with =) or by replacing parameters (SQL-style) */
export const generateQueryWithParameters = (
  query: string,
  parameterValues?: FeatureParameterValues,
  parameterConfigs?: FeatureParameterConfig[]
) => {
  if (!parameterValues) return query

  // If the query starts with '=' or if it has mustache expressions in it, evaluate it as a JEXL expression
  const evaluatedQuery =
    jexlEvalString(query, { ...parameterValues, params: parameterValues }) || query

  // Finally replace all parameters in the evaluated query
  return Object.entries(parameterValues).reduce<string>((q, [key, value]) => {
    const variableDefinition = parameterConfigs?.find((p) =>
      [p.variableName, p.beginVariableName, p.endVariableName, p.stepVariableName].includes(key)
    )
    const typed: ParameterValue | undefined = value
    // Make this work for arrays of numbers: numeric multiples are normalised element by element
    let newValue = toAdtLiteral(typed)
    if (
      newValue &&
      (Array.isArray(typed) || newValue.startsWith('[') || variableDefinition?.multiple) &&
      (variableDefinition?.kind === 'double' || variableDefinition?.kind === 'int')
    ) {
      const items: unknown[] = Array.isArray(typed) ? typed : JSON.parse(newValue)
      newValue = JSON.stringify(items.map((v) => Number.parseFloat(`${v}`)))
    }
    return q?.replaceAll(`@${key}`, `${newValue?.replaceAll('\\', '').replaceAll('"', "'")}`) ?? q
  }, evaluatedQuery)
}
